import React from "react";
import { CaptionStyles } from "../../../types";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import ColorPicker from "react-best-gradient-color-picker";

interface CaptionColorsSettingsProps {
  styles: CaptionStyles;
  onStyleChange: (field: keyof CaptionStyles, value: any) => void;
  onHighlightStyleChange: (
    field: keyof NonNullable<CaptionStyles["highlightStyle"]>,
    value: any
  ) => void;
}

/**
 * CaptionColorsSettings Component
 *
 * Provides color picker controls for caption styling:
 * - Text Color (main caption text)
 * - Highlight Background Color
 * - Highlight Text Color
 */
export const CaptionColorsSettings: React.FC<CaptionColorsSettingsProps> = ({
  styles,
  onStyleChange,
  onHighlightStyleChange,
}) => {
  return (
    <div className="space-y-3 rounded-md bg-sidebar p-2.5 border min-w-0 w-full max-w-full">
      <h5 className="text-sm font-light leading-none text-foreground">Colors</h5>

      {/* Row 1: Text Color and Highlight BG */}
      <div className="grid grid-cols-2 gap-3">
        {/* Text Color */}
        <div className="space-y-2 min-w-0">
          <label className="text-xs text-muted-foreground truncate block">Text</label>
          <Popover>
            <PopoverTrigger asChild>
              <div
                className="h-8 w-8 rounded-md border cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all"
                style={{ backgroundColor: styles.color }}
              />
            </PopoverTrigger>
            <PopoverContent className="w-[280px]" side="right">
              <ColorPicker
                value={styles.color}
                onChange={(color) => onStyleChange("color", color)}
                hideHue
                hideControls
                hideColorTypeBtns
                hideAdvancedSliders
                hideColorGuide
                hideInputType
                height={200}
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Highlight Background Color */}
        <div className="space-y-2 min-w-0">
          <label className="text-xs text-muted-foreground truncate block">Highlight BG</label>
          <Popover>
            <PopoverTrigger asChild>
              <div
                className="h-8 w-8 rounded-md border cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all"
                style={{
                  backgroundColor: styles.highlightStyle?.backgroundColor || "#3B82F6",
                }}
              />
            </PopoverTrigger>
            <PopoverContent className="w-[280px]" side="right">
              <ColorPicker
                value={styles.highlightStyle?.backgroundColor || "#3B82F6"}
                onChange={(color) =>
                  onHighlightStyleChange("backgroundColor", color)
                }
                hideHue
                hideControls
                hideColorTypeBtns
                hideAdvancedSliders
                hideColorGuide
                hideInputType
                height={200}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Row 2: Highlight Text Color */}
      <div className="min-w-0">
        <div className="space-y-2 min-w-0">
          <label className="text-xs text-muted-foreground truncate block">Highlight Text</label>
          <Popover>
            <PopoverTrigger asChild>
              <div
                className="h-8 w-8 rounded-md border cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all"
                style={{
                  backgroundColor: styles.highlightStyle?.color || "#FFFFFF",
                }}
              />
            </PopoverTrigger>
            <PopoverContent className="w-[280px]" side="right">
              <ColorPicker
                value={styles.highlightStyle?.color || "#FFFFFF"}
                onChange={(color) => onHighlightStyleChange("color", color)}
                hideHue
                hideControls
                hideColorTypeBtns
                hideAdvancedSliders
                hideColorGuide
                hideInputType
                height={200}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  );
};
